package de.xtl.retroguide

import de.xtl.retroguide.protocol.AnswerCluster
import de.xtl.retroguide.protocol.UpdateClustersRequest
import de.xtl.retroguide.protocol.CreateSessionRequest
import de.xtl.retroguide.protocol.JoinSessionRequest
import de.xtl.retroguide.protocol.SessionRoster
import de.xtl.retroguide.protocol.SessionAccess
import de.xtl.retroguide.protocol.SessionSnapshot
import de.xtl.retroguide.protocol.StartRetroRequest
import de.xtl.retroguide.protocol.StartRetroQuestion
import de.xtl.retroguide.protocol.StartQuestionTimerRequest
import de.xtl.retroguide.protocol.SubmitAnswerRequest
import de.xtl.retroguide.protocol.SetAnswerChoiceRequest
import de.xtl.retroguide.protocol.SetQuestionAnswerVisibilityRequest
import de.xtl.retroguide.protocol.ParticipantAnswerChoice
import de.xtl.retroguide.protocol.UpdateParticipantNamesRequest
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.plugins.websocket.webSocket
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import io.ktor.websocket.CloseReason
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import io.ktor.websocket.send
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

enum class RetroApiError {
    INVALID_INPUT,
    SESSION_NOT_FOUND,
    UNAUTHORIZED,
    INVALID_STATE,
    NAME_UNAVAILABLE,
    RATE_LIMITED,
    CONNECTION,
}

sealed interface RetroApiResult<out T> {
    data class Success<T>(val value: T) : RetroApiResult<T>
    data class Error(val reason: RetroApiError) : RetroApiResult<Nothing>
}

interface RetroRemoteDataSource {
    suspend fun createSession(displayName: String): RetroApiResult<SessionAccess>
    suspend fun joinSession(code: String, displayName: String): RetroApiResult<SessionAccess>
    suspend fun loadRoster(code: String): RetroApiResult<SessionRoster>
    suspend fun loadSession(access: SessionAccess): RetroApiResult<SessionSnapshot>
    suspend fun updateParticipantNames(access: SessionAccess, names: List<String>): RetroApiResult<Unit>
    suspend fun leaveSession(access: SessionAccess): RetroApiResult<SessionSnapshot?>
    suspend fun startRetro(access: SessionAccess, questions: List<StartRetroQuestion>): RetroApiResult<Unit>
    suspend fun startCurrentQuestionTimer(access: SessionAccess, durationSeconds: Int): RetroApiResult<Unit>
    suspend fun setCurrentQuestionAnswerVisibility(
        access: SessionAccess,
        showAllParticipantAnswers: Boolean,
    ): RetroApiResult<Unit>
    suspend fun submitAnswer(access: SessionAccess, content: String): RetroApiResult<Unit>
    suspend fun setAnswerChoice(access: SessionAccess, choice: ParticipantAnswerChoice?): RetroApiResult<Unit>
    suspend fun updateAnswer(access: SessionAccess, answerId: String, content: String): RetroApiResult<Unit>
    suspend fun deleteAnswer(access: SessionAccess, answerId: String): RetroApiResult<Unit>
    suspend fun revealAnswers(access: SessionAccess): RetroApiResult<Unit>
    suspend fun updateClusters(access: SessionAccess, clusters: List<AnswerCluster>): RetroApiResult<Unit>
    suspend fun nextQuestion(access: SessionAccess): RetroApiResult<Unit>
    fun session(access: SessionAccess): Flow<SessionSnapshot>
    fun roster(code: String): Flow<SessionRoster>
    fun close()
}

internal val retroJson = Json {
    ignoreUnknownKeys = true
    coerceInputValues = true
}

class KtorRetroRemoteDataSource(private val baseUrl: String) : RetroRemoteDataSource {
    private val json = retroJson
    private val client = HttpClient { install(WebSockets) }

    override suspend fun createSession(displayName: String): RetroApiResult<SessionAccess> = request(
        path = "/api/sessions",
        body = json.encodeToString(CreateSessionRequest(displayName)),
    )

    override suspend fun joinSession(code: String, displayName: String): RetroApiResult<SessionAccess> = request(
        path = "/api/sessions/$code/participants",
        body = json.encodeToString(JoinSessionRequest(displayName)),
        conflictError = RetroApiError.NAME_UNAVAILABLE,
    )

    override suspend fun loadRoster(code: String): RetroApiResult<SessionRoster> = request(
        path = "/api/sessions/$code/roster",
        body = "{}",
    )

    override suspend fun loadSession(access: SessionAccess): RetroApiResult<SessionSnapshot> = request(
        path = "/api/sessions/${access.sessionCode}/snapshot",
        body = "{}",
        accessToken = access.accessToken,
    )

    override suspend fun updateParticipantNames(access: SessionAccess, names: List<String>) = command(
        path = "/api/sessions/${access.sessionCode}/participant-names",
        body = json.encodeToString(UpdateParticipantNamesRequest(names)),
        accessToken = access.accessToken,
    )

    override suspend fun leaveSession(access: SessionAccess): RetroApiResult<SessionSnapshot?> = execute(
        path = "/api/sessions/${access.sessionCode}/leave",
        body = "{}",
        accessToken = access.accessToken,
    ).map { response ->
        response.bodyAsText().takeIf(String::isNotBlank)?.let(json::decodeFromString)
    }

    override suspend fun startRetro(access: SessionAccess, questions: List<StartRetroQuestion>) = command(
        path = "/api/sessions/${access.sessionCode}/start",
        body = json.encodeToString(StartRetroRequest(questions)),
        accessToken = access.accessToken,
    )

    override suspend fun startCurrentQuestionTimer(access: SessionAccess, durationSeconds: Int) = command(
        path = "/api/sessions/${access.sessionCode}/timer",
        body = json.encodeToString(StartQuestionTimerRequest(durationSeconds)),
        accessToken = access.accessToken,
    )

    override suspend fun setCurrentQuestionAnswerVisibility(
        access: SessionAccess,
        showAllParticipantAnswers: Boolean,
    ) = command(
        path = "/api/sessions/${access.sessionCode}/answer-visibility",
        body = json.encodeToString(SetQuestionAnswerVisibilityRequest(showAllParticipantAnswers)),
        accessToken = access.accessToken,
    )

    override suspend fun submitAnswer(access: SessionAccess, content: String) = command(
        path = "/api/sessions/${access.sessionCode}/answer",
        body = json.encodeToString(SubmitAnswerRequest(content)),
        accessToken = access.accessToken,
    )

    override suspend fun setAnswerChoice(access: SessionAccess, choice: ParticipantAnswerChoice?) = command(
        path = "/api/sessions/${access.sessionCode}/answer-choice",
        body = json.encodeToString(SetAnswerChoiceRequest(choice)),
        accessToken = access.accessToken,
    )

    override suspend fun updateAnswer(access: SessionAccess, answerId: String, content: String) = command(
        path = "/api/sessions/${access.sessionCode}/answer/$answerId",
        body = json.encodeToString(SubmitAnswerRequest(content)),
        accessToken = access.accessToken,
    )

    override suspend fun deleteAnswer(access: SessionAccess, answerId: String) = command(
        path = "/api/sessions/${access.sessionCode}/answer/$answerId/delete",
        body = "{}",
        accessToken = access.accessToken,
    )

    override suspend fun updateClusters(access: SessionAccess, clusters: List<AnswerCluster>) = command(
        path = "/api/sessions/${access.sessionCode}/clusters",
        body = json.encodeToString(UpdateClustersRequest(clusters)),
        accessToken = access.accessToken,
    )

    override suspend fun revealAnswers(access: SessionAccess) = command(
        path = "/api/sessions/${access.sessionCode}/reveal",
        body = "{}",
        accessToken = access.accessToken,
    )

    override suspend fun nextQuestion(access: SessionAccess) = command(
        path = "/api/sessions/${access.sessionCode}/next",
        body = "{}",
        accessToken = access.accessToken,
    )

    override fun session(access: SessionAccess) = live(
        snapshot = { loadSession(access) },
        websocket = socket<SessionSnapshot>(
            path = "/ws/sessions/${access.sessionCode}",
            firstFrame = access.accessToken,
        ),
    )

    override fun roster(code: String) = live(
        snapshot = { loadRoster(code) },
        websocket = socket<SessionRoster>("/ws/sessions/$code/roster"),
    )

    override fun close() = client.close()

    private suspend inline fun <reified T> request(
        path: String,
        body: String,
        accessToken: String? = null,
        conflictError: RetroApiError = RetroApiError.INVALID_STATE,
    ): RetroApiResult<T> = execute(path, body, accessToken, conflictError).map { response ->
        json.decodeFromString(response.bodyAsText())
    }

    private suspend fun command(
        path: String,
        body: String,
        accessToken: String,
    ): RetroApiResult<Unit> = execute(path, body, accessToken).map { }

    private suspend fun execute(
        path: String,
        body: String,
        accessToken: String? = null,
        conflictError: RetroApiError = RetroApiError.INVALID_STATE,
    ): RetroApiResult<HttpResponse> = try {
        val response = client.post("$baseUrl$path") {
            contentType(ContentType.Application.Json)
            accessToken?.let { header(HttpHeaders.Authorization, "Bearer $it") }
            setBody(body)
        }
        if (response.status.isSuccess()) {
            RetroApiResult.Success(response)
        } else {
            RetroApiResult.Error(response.status.toApiError(conflictError))
        }
    } catch (exception: CancellationException) {
        throw exception
    } catch (_: Exception) {
        RetroApiResult.Error(RetroApiError.CONNECTION)
    }

    private fun <T> live(
        snapshot: suspend () -> RetroApiResult<T>,
        websocket: Flow<T>,
    ): Flow<T> = channelFlow {
        launch { websocket.collect { send(it) } }
        // ponytail: polling is the reliable fallback for broken platform sockets; health-gate it if traffic grows.
        while (currentCoroutineContext().isActive) {
            delay(1_000L)
            when (val result = snapshot()) {
                is RetroApiResult.Success -> send(result.value)
                is RetroApiResult.Error -> Unit
            }
        }
    }.distinctUntilChanged()

    private inline fun <reified T> socket(path: String, firstFrame: String? = null): Flow<T> = flow {
        var retryDelay = 1_000L
        while (currentCoroutineContext().isActive) {
            var terminalClose = false
            try {
                client.webSocket("${baseUrl.replaceFirst("http", "ws")}$path") {
                    firstFrame?.let { send(Frame.Text(it)) }
                    retryDelay = 1_000L
                    for (frame in incoming) {
                        if (frame is Frame.Text) emit(json.decodeFromString<T>(frame.readText()))
                    }
                    terminalClose = closeReason.await()?.code == CloseReason.Codes.VIOLATED_POLICY.code
                }
            } catch (exception: CancellationException) {
                throw exception
            } catch (_: Exception) {
                // Retried below; the last snapshot remains visible while offline.
            }
            if (terminalClose) return@flow
            delay(retryDelay)
            retryDelay = (retryDelay * 2).coerceAtMost(30_000L)
        }
    }
}

private inline fun <T, R> RetroApiResult<T>.map(transform: (T) -> R): RetroApiResult<R> = when (this) {
    is RetroApiResult.Error -> this
    is RetroApiResult.Success -> RetroApiResult.Success(transform(value))
}

private fun HttpStatusCode.toApiError(conflictError: RetroApiError) = when (this) {
    HttpStatusCode.BadRequest -> RetroApiError.INVALID_INPUT
    HttpStatusCode.NotFound -> RetroApiError.SESSION_NOT_FOUND
    HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> RetroApiError.UNAUTHORIZED
    HttpStatusCode.Conflict -> conflictError
    HttpStatusCode.TooManyRequests -> RetroApiError.RATE_LIMITED
    else -> RetroApiError.CONNECTION
}
